import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";

import { Row, Col } from "react-bootstrap";
import { Select } from "@veneer/core";
import { HelpModalPopup } from "@components/home-page/shared-components/modal-popup/help-modal-popup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.min.css";
import { useTranslation } from 'react-i18next';
import moment from "moment";
import { apiPost } from "@services/service";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "@store/hooks";
import type { RootState } from "@store/store";

import "./customer-availability.css";

import { scrollToErrorField } from "../../../../utils";

interface CustomerAvailabilityRef {
  submitForm: () => boolean;
}

interface CustomerAvailabilityProps {
  productInfo: any;
  onFormChange?: (data: CustomerAvailabilityForm, isValid: boolean) => void;
}

interface CustomerAvailabilityForm {
  preferredContactMethod: string;
  availabilityDate: string;
  bestTimeToContact: string;
}

const DATE_ONLY_FORMAT = "YYYY-MM-DD";

const CustomerAvailability = forwardRef<
  CustomerAvailabilityRef,
  CustomerAvailabilityProps
>(({ onFormChange, productInfo }, ref) => {
  const [searchParams] = useSearchParams();
  const { formShippingAddressData } = useAppSelector(
    (state: RootState) => state.formShippingAddressInfo
  );
  const parsedData = useMemo(() => {
    try {
      return JSON.parse(productInfo?.customerAvailabilityDetails || "{}");
    } catch (error) {
      console.error("Invalid customerAvailabilityDetails JSON", error);

      return {};
    }
  }, [productInfo]);
  const { t } = useTranslation();
  const [date, setDate] = useState<Date | null>(null);
  const [selectedContactMethod, setSelectedContactMethod] = useState<any | null>(null);
  const [contactMethodSelectKey, setContactMethodSelectKey] = useState(0);

  const [selectedBestTime, setSelectedBestTime] = useState<any | null>(null);
  const [bestTimeSelectKey, setBestTimeSelectKey] = useState(0);
  const [apiBestTimeOptions, setApiBestTimeOptions] = useState<any[]>([]);
  const [disableFields, setDisableFields] = useState(false);
  const [popupTitle, setPopupTitle] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({
    contactMethod: "",
    availabilityDate: "",
    bestTime: "",
  });
  const [formData, setFormData] = useState<CustomerAvailabilityForm>({
    preferredContactMethod: "",
    availabilityDate: "",
    bestTimeToContact: "",
  });
  const applicableDays = Number(parsedData?.IsApplicable || 0);
  const rawEquipmentContactId =
    formShippingAddressData?.UserId?.[0] || formShippingAddressData?.UserId || "";
  const selectedEquipmentContactId = String(rawEquipmentContactId) === "-1" ? "" : rawEquipmentContactId;
  const shouldHideCustomerAvailability =
    applicableDays === -1 ||
    (!formShippingAddressData?.isEquipmentContactSelected && !formShippingAddressData?.NewEquipmentContact);
  const previousEquipmentContactIdRef = useRef<string>(selectedEquipmentContactId);

  const isBestTimeNAOption = (option: any) => {
    const normalize = (value: any) =>
      String(value || "")
        .toLowerCase()
        .replace(/\s+/g, "");

    const label = normalize(option?.label ?? option?.Value);
    const value = normalize(option?.value ?? option?.Key);

    return (
      label.includes("n/a") ||
      label === "na" ||
      label.includes("isna") ||
      value.includes("n/a") ||
      value === "na" ||
      value.includes("isna")
    );
  };

  useEffect(() => {
    // This logic is for isApplicable 0 where date changes happens after time selections. contract, i.e passed timeslot, where user selects date first and then time slot. If user selects today and time slot has already passed, then show error message.
    if (applicableDays !== 0 || !date || !selectedBestTime) {
      return;
    }
    const selectedMoment = moment(date);

    const isTodaySelected = selectedMoment.isSame(moment(), "day");

    if (!isTodaySelected) {
      setErrors((prev) => ({
        ...prev,
        bestTime: "",
      }));

      return;
    }

    const isNA = selectedBestTime?.label?.toLowerCase()?.includes("n/a");

    // N/A always allowed
    if (isNA) {
      return;
    }

    const timeRange = selectedBestTime.label.split("–");

    if (timeRange.length < 2) {
      return;
    }

    const [endHour, endMinute] = timeRange[1].trim().split(":").map(Number);

    const slotEndMinutes = endHour * 60 + endMinute;

    const currentMinutes = moment().hour() * 60 + moment().minute();

    // this logic is for isApplicable 0, i.e passed timeslot
    // if (slotEndMinutes <= currentMinutes) {
    //   setSelectedBestTime(null);

    //   updateFormData({
    //     bestTimeToContact: "",
    //   });

    //   setErrors((prev) => ({
    //     ...prev,
    //     bestTime: t("HPI.CreateCase.CustomerAvailability.PassedTimeSlot", {defaultValue: "Selected time slot has already passed for today."}),
    //   }));
    // } else {
    //   setErrors((prev) => ({
    //     ...prev,
    //     bestTime: "",
    //   }));
    // }
  }, [date, selectedBestTime, applicableDays]);
  const preferredContactOptions = useMemo(() => {
    return (
      parsedData?.Contactmethod?.map((item: any) => ({
        label: item.Value,
        value: item.Key,
      })) || []
    );
  }, []);

  const normalizeBestTimeOptions = (rawResponse: any) => {
    let parsed =
      rawResponse?.data?.bestTimeSlots ??
      rawResponse?.data?.Besttimetocontact ??
      rawResponse?.data ??
      rawResponse;

    const tryParseJson = (value: any) => {
      if (typeof value !== "string") {
        return value;
      }

      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    parsed = tryParseJson(parsed);

    const nestedCustomerAvailability =
      parsed?.customerAvailability ?? rawResponse?.data?.customerAvailability;

    if (nestedCustomerAvailability) {
      parsed = tryParseJson(nestedCustomerAvailability);
    }

    const bestTimeContainer =
      parsed?.Besttimetocontact ?? parsed?.bestTimeSlots ?? parsed;

    parsed = tryParseJson(bestTimeContainer);

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = [];
      }
    }

    if (Array.isArray(parsed)) {
      return parsed
        .map((item: any) => ({
          label: item?.Value ?? item?.value ?? "",
          value: String(item?.Key ?? item?.key ?? item?.value ?? ""),
        }))
        .filter((item: any) => item.label && item.value);
    }

    if (parsed && typeof parsed === "object") {
      return Object.entries(parsed).map(([key, value]) => ({
        value: String(key),
        label: String(value ?? ""),
      }));
    }

    return [];
  };

  const fetchBestTimeSlotsByDate = async (selectedDate: moment.Moment) => {
    const endpoint = "/customerportal/createcase/v1/GetCustomerAvailability";

    const ainfo = searchParams.get("ainfo") || "";

    const pno = searchParams.get("pno") || "";

    const rawCustomerId =
      formShippingAddressData?.UserId?.[0] || formShippingAddressData?.UserId || "";
    const customerId = String(rawCustomerId) === "-1" ? "" : rawCustomerId;

    const payload = {
      ainfo,
      pno,
      customerId,
      availableDate: selectedDate.format(DATE_ONLY_FORMAT),
    };

    try {
      const response = await apiPost(endpoint, payload);

      const updatedOptions = normalizeBestTimeOptions(response);

      // Always bind the latest API-filtered slots, even when empty.
      setApiBestTimeOptions(updatedOptions);

      if (updatedOptions.length === 2) {
        clearBestTimeSelection();
        updateFormData({
          bestTimeToContact: "",
        });
        setErrors((prev) => ({
          ...prev,
          bestTime: t("HPI.CreateCase.CustomerAvailability.SameDayAllTimeSlotPassed", { defaultValue: "All time slots for today have passed. Please select the next available business day." }),
        }));

        return;
      }

      // If the currently selected best time is not in the updated options, clear the selection and form value. Give an error message selected time slot has already passed for today, as the most likely reason for disappearance is that the slot is no longer valid for the selected date.
      if (
        selectedBestTime &&
        !updatedOptions.some(
          (option: any) =>
            option.value === selectedBestTime.value &&
            option.label === selectedBestTime.label
        )
      ) {
        clearBestTimeSelection();
        updateFormData({
          bestTimeToContact: "",
        });
        setErrors((prev) => ({
          ...prev,
          bestTime: t("HPI.CreateCase.CustomerAvailability.PassedTimeSlot", {defaultValue: "Selected time slot has already passed for today."})
        }));
      }
    } catch (error) {
      console.error("Failed to fetch best time slots for selected date", error);
      setApiBestTimeOptions([]);
    }
  };

  const bestTimeOptions = useMemo(() => {
    const currentMoment = moment();

    const selectedMoment = date ? moment(date) : null;

    const isTodaySelected = selectedMoment?.isSame(currentMoment, "day");

    const currentTimeInMinutes =
      currentMoment.hour() * 60 + currentMoment.minute();

    const sourceBestTimeOptions =
      apiBestTimeOptions.length > 0
        ? apiBestTimeOptions
        : parsedData?.Besttimetocontact?.map((item: any) => ({
            label: item.Value,
            value: item.Key,
          })) || [];

    return (
      sourceBestTimeOptions?.map((item: any) => {
        let isDisabled = false;

        const itemLabel = item?.label || item?.Value || "";
        const itemValue = item?.value || item?.Key || "";
        const isNA = itemLabel?.toLowerCase()?.includes("n/a");

        //this logic is for isApplicable 0, Disable only when:
        //applicableDays = 0
        //selected date = today
        //not N/A option
        if (applicableDays === 0 && !isTodaySelected) {
        }
        if (applicableDays === 0 && isTodaySelected && !isNA) {
          const timeRange = itemLabel.split("–");

          if (timeRange.length >= 2) {
            const [endHour, endMinute] = timeRange[1]
              .trim()
              .split(":")
              .map(Number);

            const endTimeInMinutes = endHour * 60 + endMinute;

            isDisabled = endTimeInMinutes <= currentTimeInMinutes;
          }
        }

        return {
          label: itemLabel,
          value: itemValue,
          disabled: false, // Disable the option in the UI, but keep it selectable for form validation purposes.
        };
      }) || []
    );
  }, [applicableDays, date, parsedData, apiBestTimeOptions]);

  const minimumSelectableDate = useMemo(() => {
    const isWeekend = (dateToCheck: moment.Moment) =>
      dateToCheck.day() === 0 || dateToCheck.day() === 6;

    const adjustToNextBusinessDay = (dateToAdjust: moment.Moment) => {
      while (isWeekend(dateToAdjust)) {
        dateToAdjust.add(1, "day");
      }

      return dateToAdjust.startOf("day");
    };

    if (applicableDays === 0) {
      return adjustToNextBusinessDay(moment().startOf("day"));
    }

    if (applicableDays === 1) {
      let today = moment().startOf("day");

      if (isWeekend(today)) {
        today = adjustToNextBusinessDay(today);
      }

      const nextDay = today.clone().add(1, "day");

      return isWeekend(nextDay)
        ? adjustToNextBusinessDay(nextDay)
        : nextDay.startOf("day");
    }

    if (applicableDays === 3) {
      // Count 3 days from tomorrow, then take the next business day.
      let countedDate = moment().add(1, "day").startOf("day");
      let daysCounted = isWeekend(countedDate) ? 0 : 1;

      while (daysCounted < applicableDays) {
        countedDate.add(1, "day");

        if (!isWeekend(countedDate)) {
          daysCounted++;
        }
      }

      countedDate = countedDate.add(1, "day");

      return adjustToNextBusinessDay(countedDate);
    }

    return adjustToNextBusinessDay(moment().add(applicableDays, "day").startOf("day"));
  }, [applicableDays]);

  const updateFormData = (updatedData: Partial<CustomerAvailabilityForm>) => {
    const normalizedAvailabilityDate =
      updatedData.availabilityDate === undefined
        ? formData.availabilityDate
        : updatedData.availabilityDate
          ? moment(updatedData.availabilityDate, DATE_ONLY_FORMAT, true).isValid()
            ? moment(updatedData.availabilityDate, DATE_ONLY_FORMAT, true).format(
                DATE_ONLY_FORMAT
              )
            : ""
          : "";

    const updatedForm = {
      ...formData,
      ...updatedData,
      availabilityDate: normalizedAvailabilityDate,
    };

    setFormData(updatedForm);

    const isValid =
      !!updatedForm.bestTimeToContact &&
      (disableFields ||
        (!!updatedForm.preferredContactMethod &&
          !!updatedForm.availabilityDate));

    onFormChange?.(updatedForm, isValid);
  };

  const clearContactMethodSelection = () => {
    setSelectedContactMethod(null);
    setContactMethodSelectKey((prev) => prev + 1);
  };

  const clearBestTimeSelection = () => {
    setSelectedBestTime(null);
    setBestTimeSelectKey((prev) => prev + 1);
  };

  const clearCustomerAvailabilityForm = () => {
    clearBestTimeSelection();
    clearContactMethodSelection();
    setDate(null);
    setDisableFields(false);
    setApiBestTimeOptions([]);

    const resetFormData: CustomerAvailabilityForm = {
      preferredContactMethod: "",
      availabilityDate: "",
      bestTimeToContact: "",
    };

    setFormData(resetFormData);
    onFormChange?.(resetFormData, false);
    setErrors({
      contactMethod: "",
      availabilityDate: "",
      bestTime: "",
    });
  };

  useEffect(() => {
    if (previousEquipmentContactIdRef.current === selectedEquipmentContactId) {
      return;
    }

    previousEquipmentContactIdRef.current = selectedEquipmentContactId;
    clearCustomerAvailabilityForm();
  }, [selectedEquipmentContactId]);

  useEffect(() => {
    if (!disableFields) {
      return;
    }

    // Keep disabled fields visually and functionally cleared.
    clearContactMethodSelection();
    setDate(null);
    updateFormData({
      preferredContactMethod: "",
      availabilityDate: "",
    });
    setErrors((prev) => ({
      ...prev,
      contactMethod: "",
      availabilityDate: "",
    }));
  }, [disableFields]);

  const validateForm = () => {
    if (shouldHideCustomerAvailability) {
      return true;
    }

    let isValid = true;

    let firstErrorField = "";

    const newErrors = {
      contactMethod: "",
      availabilityDate: "",
      bestTime: "",
    };

    if (!selectedBestTime) {
      newErrors.bestTime = t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' });

      isValid = false;

      firstErrorField = "best-time-contact";
    }

    if (!disableFields) {
      if (!selectedContactMethod) {
        newErrors.contactMethod = t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' });

        isValid = false;

        firstErrorField = firstErrorField || "preferred-contact-method";
      }

      if (!date) {
        newErrors.availabilityDate = t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' });

        isValid = false;

        firstErrorField = firstErrorField || "customer-availability-date";
      }
    }

    setErrors(newErrors);

    if (!isValid && firstErrorField) {
      scrollToErrorField(firstErrorField);
    }

    return isValid;
  };

  useImperativeHandle(ref, () => ({
    submitForm: validateForm,
  }));

  const handleContactMethodChange = (option: any) => {
    setSelectedContactMethod(option);

    updateFormData({
      preferredContactMethod: option?.value || "",
    });

    setErrors((prev) => ({
      ...prev,
      contactMethod: option ? "" : t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' }),
    }));
  };

  const handleBestTimeChange = (option: any) => {
    if (!option) {
      clearBestTimeSelection();
    } else {
      setSelectedBestTime(option);
    }

    const isNA = isBestTimeNAOption(option);

    setDisableFields(!!isNA);

    //if option N/A selected in best time to contact then clear all field related to customer availability and preferred contact method and also clear errors related to that and return from here, as other fields will be disabled.
    if (isNA) {
      clearContactMethodSelection();

      setDate(null);

      updateFormData({
        preferredContactMethod: "",
        availabilityDate: "",
        bestTimeToContact: option?.value || "",
      });

      setErrors((prev) => ({
        ...prev,
        contactMethod: "",
        availabilityDate: "",
        bestTime: "",
      }));

      return;
    }

    updateFormData({
      bestTimeToContact: option?.value || "",
    });

    setErrors((prev) => ({
      ...prev,
      bestTime: "",
    }));
  };

  const handleDateChange = (selectedDate: Date | null) => {
    if (!selectedDate) {
      setDate(null);

      updateFormData({
        availabilityDate: "",
      });

      setErrors((prev) => ({
        ...prev,
        availabilityDate: t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' }),
      }));

      return;
    }

    const selectedMoment = moment(selectedDate);

    const isWeekend = selectedMoment.day() === 0 || selectedMoment.day() === 6;

    const isBlocked = selectedMoment.isBefore(minimumSelectableDate, "day");

    if (isWeekend || isBlocked) {
      return;
    }

    const isTodaySelected = selectedMoment.isSame(moment(), "day");

    if (!isTodaySelected) {
      setErrors((prev) => ({
        ...prev,
        bestTime: "",
        availabilityDate: "",
      }));
    } 

    if (isTodaySelected && selectedBestTime) {
      const isNA = isBestTimeNAOption(selectedBestTime);

      if (!isNA) {
        const timeRange = selectedBestTime.label.split("–");

        // if (timeRange.length >= 2) {
        //   const [endHour, endMinute] = timeRange[1]
        //     .trim()
        //     .split(":")
        //     .map(Number);

        //   const slotEndMinutes = endHour * 60 + endMinute;

        //   const currentMinutes = moment().hour() * 60 + moment().minute();

        //   //this logic is for isApplicable 0, where date changes happens after time selections.
        //   if (slotEndMinutes <= currentMinutes) {
        //     setErrors((prev) => ({
        //       ...prev,
        //       bestTime: "Selected time slot has already passed for today.",
        //     }));
        //   } else {
        //     setErrors((prev) => ({
        //       ...prev,
        //       bestTime: "",
        //     }));
        //   }
        // }
      }
    }

    setDate(selectedDate);

    // // Date changed: clear best-time selection and form value before rebinding options.
    // setSelectedBestTime(null);
    // updateFormData({
    //   bestTimeToContact: "",
    // });

    fetchBestTimeSlotsByDate(selectedMoment);

    updateFormData({
      availabilityDate: selectedMoment.format(DATE_ONLY_FORMAT),
    });

    setErrors((prev) => ({
      ...prev,
      availabilityDate: "",
    }));
  };
  if (shouldHideCustomerAvailability) {
    return null;
  }

  const popupHeader = () => (
    <p className="popup-header">
      <img
        src="https://logos-world.net/wp-content/uploads/2020/11/HP-Logo.png"
        alt=""
        style={{ height: "46px", width: "auto" }}
      />
      <span>{popupTitle}</span>
    </p>
  );
  return (
    <><div className="col-md-10 mb-4">
        <h5 className="subtitle-large">
          {t("HPI.Create.Case.Customeravailabilitytitle", {defaultValue: "Customer Availability for Onsite"})}
          <span className="supporting-text">
            <HelpModalPopup
              align="start"
              id="modal-popup"
              show={open}
              onClose={() => setOpen(false)}
              title={popupHeader()}
              callback={() =>
                setPopupTitle(
                  t("HPI.Create.Case.Customeravailabilitytitle", {defaultValue: "Customer Availability for Onsite"})
                )
              }
            >
              <div className="col-lg-24 unWrrntyModalContent">
                <ul className="modal-list">
                  <li>
                    {t("HPI.CreateCase.Disclaimer.Customeravailabilityline1", {defaultValue: "This date is not final and depends on spare parts availability & eligible service for the product. The assigned field technician will confirm the final visit date."})}
                  </li>
                  <li>
                    {t("HPI.CreateCase.Disclaimer.Customeravailabilityline2", {defaultValue: "We may attempt to reach you via phone first, If unsuccessful, we will contact you via e-mail."})}
                  </li>
                </ul>
              </div>
            </HelpModalPopup>
          </span>
        </h5>
      </div>
      <Row className="form-row">
        <Col md={3}>
          <label className="label">
            {t("HPI.CreateCase.Customeravailability.Besttimetocontact", {defaultValue: "Best Time to Contact"})}
            <span className="required">*</span>
          </label>
        </Col>

        <Col md={6} className="BestTimeCol">
          <div id="best-time-contact" className="field-width-container">
            <Select
              key={`best-time-${bestTimeSelectKey}`}
              options={bestTimeOptions}
              value={selectedBestTime ?? undefined}
              onChange={handleBestTimeChange}
              onClear={() => {
                clearBestTimeSelection();

                setDisableFields(false);

                updateFormData({
                  bestTimeToContact: "",
                });

                setErrors((prev) => ({
                  ...prev,
                  bestTime: t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' }),
                }));
              }}
              placeholder={t("HPI.Dropdown.DefaultText", {defaultValue: "Select"})}
              error={!!errors.bestTime}
              helperText={errors.bestTime}
            />
          </div>
        </Col>
      </Row>

      <Row className="form-row">
        <Col md={3}>
          <label className="label">
            {t("HPI.Create.Case.Customeravailabilitytitle", {defaultValue: "Customer Availability"})}
            {!disableFields && <span className="required">*</span>}
          </label>
        </Col>

        <Col md={6}>
          <div
            id="customer-availability-date"
            className="datepicker-field-container"
          >
            <DatePicker
              selected={date}
              onChange={handleDateChange}
              onSelect={(selectedDate) =>
                handleDateChange((selectedDate as Date | null) || null)
              }
              disabled={disableFields}
              minDate={minimumSelectableDate.toDate()}
              isClearable
              placeholderText={t("HPI.DatePicker.Placeholder", {defaultValue: "MM/DD/YYYY"})}
              dateFormat="MM/dd/yyyy"
              popperPlacement="top-start"
              className={`custom-datepicker ${
                errors.availabilityDate ? "date-error" : ""
              } ${disableFields ? "datepicker-disabled" : ""}`}
              renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                <div className="calendar-header">
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    className="calendar-nav-btn"
                  >
                    &#8249;
                  </button>

                  <span className="calendar-current-month">
                    {moment(date).format("MMMM YYYY")}
                  </span>

                  <button
                    type="button"
                    onClick={increaseMonth}
                    className="calendar-nav-btn"
                  >
                    &#8250;
                  </button>
                </div>
              )}
              filterDate={(currentDate) => {
                const day = moment(currentDate).day();

                return day !== 0 && day !== 6;
              }}
              dayClassName={(currentDate) => {
                const currentMoment = moment(currentDate);

                if (currentMoment.isSame(minimumSelectableDate, "day")) {
                  return "minimum-date";
                }

                if (currentMoment.isBefore(minimumSelectableDate, "day")) {
                  return "blocked-date";
                }

                return "";
              }}
            />

            {errors.availabilityDate && (
              <span className="datepicker-error" role="alert" aria-live="polite">
                <span className="datepicker-error-icon" aria-hidden="true">
                  !
                </span>
                <span>{errors.availabilityDate}</span>
              </span>
            )}
          </div>
        </Col>
      </Row>

      <Row className="form-row">
        <Col md={3}>
          <label className="label">
            {t("HPI.CreateCase.Customeravailability.Preferredcontactmethod", {defaultValue: "Preferred Contact Method"})}
            {!disableFields && <span className="required">*</span>}
          </label>
        </Col>

        <Col md={6}>
          <div id="preferred-contact-method" className="field-width-container">
            <Select
              key={`preferred-contact-${contactMethodSelectKey}`}
              options={preferredContactOptions}
              value={selectedContactMethod ?? undefined}
              onChange={handleContactMethodChange}
              onClear={() => {
                clearContactMethodSelection();

                setDisableFields(false);

                updateFormData({
                  preferredContactMethod: "",
                });

                setErrors((prev) => ({
                  ...prev,
                  contactMethod: t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' }),
                }));
              }}
              disabled={disableFields}
              placeholder={t("HPI.Dropdown.DefaultText", {defaultValue: "Select"})}
              error={!!errors.contactMethod}
              helperText={errors.contactMethod}
            />
          </div>
        </Col>
      </Row>
    </>
  );
});

export default CustomerAvailability;